/**
 * OpsRetrievalCriteriaPanel
 *
 * Three-column search-criteria form for the OPS retrieval modal (Panel 1).
 * Col 1: Keywords + Parties
 * Col 2: Classification (IPC only), Identifiers, Dates, Scope
 * Col 3: LLM-suggested CPC groups with checkboxes
 * Stateless: all values and handlers come from OpsRetrievalModal via props.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OpsAdvancedQuery } from '../services/opsRetrievalService';
import type { CpcSuggestion, CpcSubclassSuggestion } from '../services/subProjectService';
import {
  ChipInput,
  FieldRow,
  SectionHeading,
  TextInput,
  COUNTRY_OPTIONS,
  LANGUAGE_OPTIONS,
} from './OpsRetrievalFormParts';

type CpcPhase = 'idle' | 'loop1_loading' | 'loop2_loading' | 'complete' | 'error';

export interface OpsRetrievalCriteriaPanelProps {
  query: OpsAdvancedQuery;
  set: <K extends keyof OpsAdvancedQuery>(key: K, value: OpsAdvancedQuery[K]) => void;
  toggleCountry: (code: string) => void;
  showCql: boolean;
  setShowCql: React.Dispatch<React.SetStateAction<boolean>>;
  hasAnyCriteria: boolean;
  previewing: boolean;
  previewError: string | null;
  onClose: () => void;
  onPreview: () => void;
  cpcPhase: CpcPhase;
  cpcSubclasses: CpcSubclassSuggestion[];
  cpcSuggestions: CpcSuggestion[];
  cpcError: string | null;
  selectedCpcCodes: Set<string>;
  onToggleCpc: (code: string) => void;
  onSelectAllCpc: () => void;
  onDeselectAllCpc: () => void;
}

export default function OpsRetrievalCriteriaPanel({
  query, set, toggleCountry,
  showCql, setShowCql,
  hasAnyCriteria, previewing, previewError,
  onClose, onPreview,
  cpcPhase, cpcSubclasses, cpcSuggestions, cpcError, selectedCpcCodes,
  onToggleCpc, onSelectAllCpc, onDeselectAllCpc,
}: OpsRetrievalCriteriaPanelProps) {
  const { t } = useTranslation();

  // Group final CPC suggestions by section → subclass for hierarchical display
  type SubclassMap = Record<string, { label: string; items: CpcSuggestion[] }>;
  type SectionMap = Record<string, { label: string; subclasses: SubclassMap }>;
  const groupedCpc = cpcSuggestions.reduce<SectionMap>((acc, s) => {
    const secKey = s.section;
    const subKey = s.subclass_code || s.section;
    if (!acc[secKey]) acc[secKey] = { label: s.section_label, subclasses: {} };
    if (!acc[secKey].subclasses[subKey])
      acc[secKey].subclasses[subKey] = { label: s.subclass_label || s.class_label || '', items: [] };
    acc[secKey].subclasses[subKey].items.push(s);
    return acc;
  }, {});

  return (
    <>
      <div className="px-5 py-3 overflow-y-auto flex-1 text-sm">
        <div className="grid grid-cols-[1fr_1fr_2fr] gap-x-6">

          {/* LEFT COLUMN */}
          <div>
            <SectionHeading label={t('opsRetrieval.criteria.keywords', 'Keywords')} />
            <FieldRow label={t('opsRetrieval.criteria.titleAbstract', 'Title + Abstract')}>
              <TextInput
                placeholder="e.g. battery electrolyte"
                value={query.keywords_title_abstract ?? ''}
                onChange={(e) => set('keywords_title_abstract', e.target.value)}
              />
            </FieldRow>
            <FieldRow label={t('opsRetrieval.criteria.titleOnly', 'Title only')}>
              <TextInput
                placeholder="e.g. fuel cell"
                value={query.keywords_title ?? ''}
                onChange={(e) => set('keywords_title', e.target.value)}
              />
            </FieldRow>
            <FieldRow label={t('opsRetrieval.criteria.fullText', 'Full text')}>
              <TextInput
                placeholder="e.g. solid electrolyte"
                value={query.keywords_full_text ?? ''}
                onChange={(e) => set('keywords_full_text', e.target.value)}
              />
            </FieldRow>

            <SectionHeading label={t('opsRetrieval.criteria.parties', 'Parties')} />
            <FieldRow label={t('opsRetrieval.criteria.inventor', 'Inventor')}>
              <ChipInput
                values={query.inventor ?? []}
                onChange={(v) => set('inventor', v)}
                placeholder="Name \u2014 press Enter"
              />
            </FieldRow>
            <FieldRow label={t('opsRetrieval.criteria.applicant', 'Applicant / Owner')}>
              <ChipInput
                values={query.applicant ?? []}
                onChange={(v) => set('applicant', v)}
                placeholder="Company \u2014 press Enter"
              />
            </FieldRow>
          </div>

          {/* MIDDLE COLUMN */}
          <div>
            <SectionHeading label={t('opsRetrieval.criteria.classification', 'Classification')} />
            <FieldRow label="IPC">
              <ChipInput
                values={query.ipc ?? []}
                onChange={(v) => set('ipc', v)}
                placeholder="e.g. F16K1/00"
              />
            </FieldRow>

            <SectionHeading label={t('opsRetrieval.criteria.identifiers', 'Identifiers')} />
            <FieldRow label={t('opsRetrieval.criteria.pubNumber', 'Publication no.')}>
              <TextInput
                placeholder="e.g. EP1234567A1"
                value={query.publication_number ?? ''}
                onChange={(e) => set('publication_number', e.target.value)}
              />
            </FieldRow>
            <FieldRow label={t('opsRetrieval.criteria.appNumber', 'Application no.')}>
              <TextInput
                placeholder="e.g. EP20200123456"
                value={query.application_number ?? ''}
                onChange={(e) => set('application_number', e.target.value)}
              />
            </FieldRow>
            <FieldRow label={t('opsRetrieval.criteria.priorNumber', 'Priority no.')}>
              <TextInput
                value={query.priority_number ?? ''}
                onChange={(e) => set('priority_number', e.target.value)}
              />
            </FieldRow>

            <SectionHeading label={t('opsRetrieval.criteria.dates', 'Dates')} />
            <div className="space-y-1.5 mb-2">
              <div className="grid grid-cols-[6rem_1fr_auto_1fr] items-center gap-1">
                <span className="text-xs text-slate-500">{t('opsRetrieval.criteria.pubDate', 'Publication')}</span>
                <TextInput type="date" value={query.publication_date_from ?? ''} onChange={(e) => set('publication_date_from', e.target.value)} />
                <span className="text-slate-400 px-0.5">\u2013</span>
                <TextInput type="date" value={query.publication_date_to ?? ''} onChange={(e) => set('publication_date_to', e.target.value)} />
              </div>
              <div className="grid grid-cols-[6rem_1fr_auto_1fr] items-center gap-1">
                <span className="text-xs text-slate-500">{t('opsRetrieval.criteria.appDate', 'Application')}</span>
                <TextInput type="date" value={query.application_date_from ?? ''} onChange={(e) => set('application_date_from', e.target.value)} />
                <span className="text-slate-400 px-0.5">\u2013</span>
                <TextInput type="date" value={query.application_date_to ?? ''} onChange={(e) => set('application_date_to', e.target.value)} />
              </div>
              <div className="grid grid-cols-[6rem_1fr_auto_1fr] items-center gap-1">
                <span className="text-xs text-slate-500">{t('opsRetrieval.criteria.priorDate', 'Priority')}</span>
                <TextInput type="date" value={query.priority_date_from ?? ''} onChange={(e) => set('priority_date_from', e.target.value)} />
                <span className="text-slate-400 px-0.5">\u2013</span>
                <TextInput type="date" value={query.priority_date_to ?? ''} onChange={(e) => set('priority_date_to', e.target.value)} />
              </div>
            </div>

            <SectionHeading label={t('opsRetrieval.criteria.scope', 'Scope')} />
            <FieldRow label={t('opsRetrieval.criteria.countries', 'Countries')}>
              <div className="flex flex-wrap gap-2">
                {COUNTRY_OPTIONS.map(({ code, label }) => (
                  <label key={code} className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(query.country_codes ?? []).includes(code)}
                      onChange={() => toggleCountry(code)}
                      className="rounded"
                    />
                    <span title={label}>{code}</span>
                  </label>
                ))}
              </div>
            </FieldRow>
            <FieldRow label={t('opsRetrieval.criteria.language', 'Language')}>
              <select
                className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                value={query.language ?? ''}
                onChange={(e) => set('language', e.target.value || undefined)}
              >
                {LANGUAGE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </FieldRow>
          </div>

          {/* RIGHT COLUMN — CPC Suggestions */}
          <div className="border-l border-slate-200 pl-4">
            <div className="flex items-center justify-between mb-1.5">
              <SectionHeading label={t('opsRetrieval.cpc.title', 'Suggested CPC Classes')} />
            </div>

            {/* Phase: loading Loop 1 */}
            {(cpcPhase === 'idle' || cpcPhase === 'loop1_loading') && (
              <div className="flex items-center gap-2 py-4 text-slate-400 text-xs">
                <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                {t('opsRetrieval.cpc.loading', 'Analyzing problem statement...')}
              </div>
            )}

            {/* Phase: Loop 1 done, Loop 2 running — show subclass summary */}
            {cpcPhase === 'loop2_loading' && (
              <div>
                <p className="text-xs text-slate-600 mb-2">
                  {t('opsRetrieval.cpc.refining', `${cpcSubclasses.length} relevant subclasses identified. Refining to specific codes — please be patient...`)}
                </p>
                <div className="space-y-1 max-h-[52vh] overflow-y-auto pr-1 mb-2">
                  {cpcSubclasses.map((sc) => (
                    <div key={sc.code} className="text-xs text-slate-500 flex gap-1.5">
                      <span className="font-mono text-slate-700 font-medium flex-shrink-0">{sc.code}</span>
                      <span>{sc.title}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  {t('opsRetrieval.cpc.refiningGroups', 'Selecting specific groups...')}
                </div>
              </div>
            )}

            {/* Phase: error */}
            {cpcPhase === 'error' && cpcError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-2">{cpcError}</p>
            )}

            {/* Phase: complete — final checkboxes */}
            {cpcPhase === 'complete' && cpcSuggestions.length === 0 && (
              <p className="text-xs text-slate-400 py-2">{t('opsRetrieval.cpc.empty', 'No suggestions available')}</p>
            )}

            {cpcPhase === 'complete' && cpcSuggestions.length > 0 && (
              <>
                <div className="flex gap-2 mb-2">
                  <button type="button" className="text-xs text-blue-600 hover:underline" onClick={onSelectAllCpc}>
                    {t('opsRetrieval.cpc.selectAll', 'Select all')}
                  </button>
                  <button type="button" className="text-xs text-slate-500 hover:underline" onClick={onDeselectAllCpc}>
                    {t('opsRetrieval.cpc.deselectAll', 'Deselect all')}
                  </button>
                </div>
                <div className="space-y-3 max-h-[58vh] overflow-y-auto pr-1">
                  {Object.entries(groupedCpc).sort(([a], [b]) => a.localeCompare(b)).map(([section, { label: sectionLabel, subclasses }]) => (
                    <div key={section}>
                      <p className="text-xs font-bold text-slate-700 bg-slate-100 px-2 py-1 rounded mb-1.5">
                        {section} &mdash; {sectionLabel}
                      </p>
                      {Object.entries(subclasses).sort(([a], [b]) => a.localeCompare(b)).map(([subclass, { label: subclassLabel, items }]) => (
                        <div key={subclass} className="ml-2 mb-2">
                          <p className="text-xs font-semibold text-slate-500 border-l-2 border-slate-200 pl-2 mb-1">
                            {subclass}{subclassLabel && ` — ${subclassLabel}`}
                          </p>
                          <div className="space-y-0.5 pl-1">
                            {items.map((s) => (
                              <label key={s.code} className="flex items-start gap-1.5 text-xs cursor-pointer py-0.5 hover:bg-blue-50 rounded px-1">
                                <input
                                  type="checkbox"
                                  checked={selectedCpcCodes.has(s.code)}
                                  onChange={() => onToggleCpc(s.code)}
                                  className="rounded mt-0.5 flex-shrink-0"
                                />
                                <span>
                                  <span className="font-mono text-slate-800 font-medium">{s.code}</span>
                                  <span className="text-slate-500 ml-1.5">{s.label}</span>
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* CQL preview (collapsible) */}
        <div className="mt-3">
          <button
            type="button"
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            onClick={() => setShowCql((v) => !v)}
          >
            {showCql ? t('opsRetrieval.criteria.hideCql', 'Hide CQL query') : t('opsRetrieval.criteria.showCql', 'Show CQL query')}
          </button>
          {showCql && (
            <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded text-xs font-mono whitespace-pre-wrap break-all text-slate-600">
              {t('opsRetrieval.criteria.cqlNote', 'CQL will be generated when you click Preview Count')}
            </pre>
          )}
        </div>

        {previewError && (
          <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{previewError}</p>
        )}
      </div>

      <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 flex-shrink-0">
        <button type="button" className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200" onClick={onClose}>
          {t('common.cancel', 'Cancel')}
        </button>
        <button
          type="button"
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={!hasAnyCriteria || previewing}
          onClick={onPreview}
        >
          {previewing ? t('opsRetrieval.criteria.previewing', 'Querying OPS\u2026') : t('opsRetrieval.criteria.previewBtn', 'Preview Count')}
        </button>
      </div>
    </>
  );
}
